/**
 * Flyways map — tuneable camera, bird/label placements, and motion.
 *
 * Cameras: fractional pan (`x`/`y`) + `scale`, origin at map centre.
 * Applied as SVG `viewBox` via `cameraToViewBox` in `@/constants/flywaysMap`.
 *   x > 0 → show more of the left side; y > 0 → more of the top.
 * LTR uses `flywayMapCamera`; RTL (`/ar`) uses `flywayMapCameraRtl`.
 * Compact (`< lg`) lerps `flywayMapCameraMobile` → `flywayMapCameraMobileWide`
 * by camera-box width (narrow ~390 → wide ~1023); shared for LTR + RTL.
 *
 * Placements: SVG viewBox units (1705×1195). Bird `x`/`y` is the transform
 * origin (top-left before rotate/flip). Label `x`/`y` is the label plate’s
 * top-left. Applied on load via `applyFlywaysMapPlacements`.
 *
 * Filter change choreography (seconds from transition start):
 *   0 ──────────────────────────── cameraSeconds
 *   │ camera pan/zoom ──────────────────────────│
 *   0 ────── fadeOutSeconds
 *   │ fade previous layers out ──│
 *   └── fadeInDelaySeconds ────── fadeInSeconds
 *       │ prepare + fade layers in ──│
 *
 * Keep `fadeInDelaySeconds` short so the camera and outgoing fade overlap,
 * then new flyways appear shortly after the move begins.
 *
 * Comet trails: dashed-segment stroke animation (`flywayMapComet`).
 * Shared defaults + optional per-flyway overrides (`resolveFlywayMapComet`).
 * Hard wrap at the path seam (modulo teleport).
 */

import {
  FLYWAYS_MAP_AFRICAN_EURASIAN_LONG_PATH_IDS,
  FLYWAYS_MAP_AFRICAN_EURASIAN_SPLIT_PATH_ID,
  FLYWAYS_MAP_AFRICAN_EURASIAN_TIMING_REFERENCE_PATH_ID,
  FLYWAYS_MAP_AMERICAS_SEAMLESS_LOOP_PATH_ID,
  FLYWAYS_MAP_ARCTIC_TERN_BRANCH_LOOP_PATH_ID,
  FLYWAYS_MAP_CENTRAL_ASIA_SPLIT_PATH_ID,
  FLYWAYS_MAP_GREY_HEADED_ALBATROSS_PATH_ID,
  FLYWAYS_MAP_RUFOUS_HUMMINGBIRD_PATH_ID,
  FLYWAYS_MAP_SOUTH_POLAR_SKUA_PATH_ID,
} from '@/constants/flywaysMapPaths';

export type FlywayMapCamera = {
  /** Pan X as a fraction of the zoomed view width. */
  x: number;
  /** Pan Y as a fraction of the zoomed view height. */
  y: number;
  /** Zoom factor (≥ 1). */
  scale: number;
};

type FlywayMapCameraFilterId =
  | 'all'
  | 'americas'
  | 'african-eurasian'
  | 'central-asia'
  | 'east-asia-australasia'
  | 'marine';

/**
 * Per-filter cameras for LTR (Figma state frames vs master artboard).
 * Tweak here — no code changes needed elsewhere.
 */
export const flywayMapCamera = {
  all: { x: 0.1, y: -0.16, scale: 1 },
  americas: { x: 0.22, y: -0.08, scale: 1.3 },
  'african-eurasian': { x: 0.01, y: -0.1, scale: 1.2 },
  'central-asia': { x: -0.06, y: -0.09, scale: 1.3 },
  'east-asia-australasia': { x: -0.05, y: -0.15, scale: 1.2 },
  marine: { x: 0.11, y: -0.16, scale: 1 },
} as const satisfies Record<FlywayMapCameraFilterId, FlywayMapCamera>;

/**
 * Per-filter cameras for RTL (`/ar`). Seeded by negating LTR `x`; tune independently.
 */
export const flywayMapCameraRtl = {
  all: { x: -0.1, y: -0.16, scale: 1 },
  americas: { x: 0, y: -0.08, scale: 1.3 },
  'african-eurasian': { x: -0.2, y: 0, scale: 1.3 },
  'central-asia': { x: -0.18, y: -0.09, scale: 1.4 },
  'east-asia-australasia': { x: -0.2, y: -0.15, scale: 1.3 },
  marine: { x: -0.11, y: -0.16, scale: 1 },
} as const satisfies Record<FlywayMapCameraFilterId, FlywayMapCamera>;

/** SVG viewBox units; includes the final 13px desktop alignment adjustment. */
export const flywayMapMarineSelectedArtworkOffset = {
  x: -66.3435,
  y: 0,
} as const;

/** Nested-group SVG units, calibrated to the requested 1744px desktop view. */
export const flywayMapMarineSelectedRegionOffsets = [
  {
    groupId: 'West Pacific Ocean Flyway',
    x: -3.9936,
    y: 0,
  },
  {
    groupId: 'North Indian Ocean Flyway',
    x: -6.3897,
    y: 0,
  },
] as const;

/**
 * Compact camera at the narrow reference width
 * ({@link flywayMapCameraMobileNarrowWidth}). Shared for LTR + RTL.
 * Seeded from `flywayMapCamera`; tune independently for phone framing.
 */
export const flywayMapCameraMobile = {
  // Scales tuned to Figma UI Mobile map frames (e.g. Americas 921:140078).
  // All Flyways opens at a focused Atlantic/Africa framing (Figma 67:2117),
  // matching the selected tabs' zoom; users pan/pinch to explore the rest.
  // Marine still starts wider so its complete species set is visible.
  all: { x: -0.224, y: 0.067, scale: 1.4 },
  americas: { x: 0, y: -0.08, scale: 1.4 },
  'african-eurasian': { x: -0.35, y: -0.02, scale: 1.2 },
  'central-asia': { x: -0.44, y: 0.02, scale: 1.25 },
  'east-asia-australasia': { x: -0.63, y: -0.05, scale: 1.4 },
  marine: { x: -0.25, y: -0.02, scale: 0.65 },
} as const satisfies Record<FlywayMapCameraFilterId, FlywayMapCamera>;

/**
 * Compact camera at the wide end of `< lg`
 * ({@link flywayMapCameraMobileWideWidth}). Softens horizontal pan so
 * cover-crop framing stays centred as the viewport widens past ~390px.
 */
export const flywayMapCameraMobileWide = {
  all: { x: 0.04, y: -0.04, scale: 1.4 },
  americas: { x: 0.2, y: -0.08, scale: 1.4 },
  'african-eurasian': { x: -0.12, y: 0, scale: 1.35 },
  'central-asia': { x: -0.2, y: 0, scale: 1.45 },
  'east-asia-australasia': { x: -0.2, y: -0.05, scale: 1.4 },
  marine: { x: 0.05, y: -0.1, scale: 1.1 },
} as const satisfies Record<FlywayMapCameraFilterId, FlywayMapCamera>;

/** CSS px — `flywayMapCameraMobile` endpoint for compact width lerp. */
export const flywayMapCameraMobileNarrowWidth = 390;

/** CSS px — `flywayMapCameraMobileWide` endpoint (just below `lg` / 64rem). */
export const flywayMapCameraMobileWideWidth = 1023;

/** User pan/pinch limits on the compact gesture layer (CSS transform). */
export const flywayMapGesture = {
  minScale: 0.4,
  maxScale: 3,
  /**
   * Max pan left (negative translateX) in CSS pixels from the filter camera.
   * e.g. `80` → translateX ≥ −80.
   */
  maxPanLeft: 640,
  /**
   * Max pan right (positive translateX) in CSS pixels from the filter camera.
   * e.g. `400` → translateX ≤ 400.
   */
  maxPanRight: 280,
  /**
   * Max absolute vertical pan in CSS pixels from the filter camera origin.
   * e.g. `180` → translateY clamped to [-180, 180].
   */
  maxPanY: 80,
  /**
   * One-finger movement (CSS px) before showing the “use two fingers” tip.
   * Below this, taps / tiny jitters do not flash the tip.
   */
  oneFingerTipThresholdPx: 10,
  /** How long the two-finger tip stays visible after one-finger lift (ms). */
  twoFingerTipHideMs: 2000,
} as const;

export type FlywayMapCameraConfig = typeof flywayMapCamera;

export type FlywayMapBirdPlacement = {
  /** Transform origin X in SVG units. */
  x: number;
  /** Transform origin Y in SVG units. */
  y: number;
  /** Rotation in degrees (SVG `rotate`, around origin). */
  rotate?: number;
  /** Uniform scale factor (SVG `scale`, after rotate; default 1). */
  scale?: number;
  /** Mirror horizontally (`scale(-1 1)` after rotate/scale). */
  flipX?: boolean;
};

export type FlywayMapLabelPlacement = {
  /** Label plate top-left X in SVG units. */
  x: number;
  /** Label plate top-left Y in SVG units. */
  y: number;
};

export type FlywayMapVisibleBirdBounds = {
  /** Visible cutout inset from the source image's left edge (0–1). */
  x: number;
  /** Visible cutout inset from the source image's top edge (0–1). */
  y: number;
  /** Visible cutout width as a fraction of the source image width. */
  width: number;
  /** Visible cutout height as a fraction of the source image height. */
  height: number;
};

export type FlywayMapSpeciesPlacement = {
  bird: FlywayMapBirdPlacement;
  /** Alpha-trimmed source bounds used for label spacing and RTL alignment. */
  visibleBirdBounds?: FlywayMapVisibleBirdBounds;
  /** Optional visible-artwork gap; negative values permit deliberate overlap. */
  labelGap?: number;
  label: FlywayMapLabelPlacement;
};

type FlywayMapSpeciesId =
  | 'hudsonian-godwit'
  | 'rufous-hummingbird'
  | 'swainsons-hawk'
  | 'pacific-swift'
  | 'rainbow-bee-eater'
  | 'far-eastern-curlew'
  | 'red-footed-falcon'
  | 'common-eider'
  | 'northern-wheatear'
  | 'indian-paradise-flycatcher'
  | 'sociable-lapwing'
  | 'bar-headed-goose'
  | 'grey-headed-albatross'
  | 'arctic-tern'
  | 'south-polar-skua';

/**
 * Bird image + species label positions (SVG viewBox units).
 * Tweak here — applied on map load over the SVG’s baked transforms.
 */
export const flywayMapPlacements = {
  'hudsonian-godwit': {
    bird: { x: 551.019, y: 321.486 },
    visibleBirdBounds: {
      x: 0.053333,
      y: 0.030844,
      width: 0.873333,
      height: 0.839286,
    },
    label: { x: 539.5, y: 423.678 },
  },
  'rufous-hummingbird': {
    // Complex Figma matrix — keep verified rotate (matches prior facing)
    bird: { x: 324.7, y: 444, rotate: 25.805, scale: 0.7 },
    visibleBirdBounds: {
      x: 0.145,
      y: 0.053371,
      width: 0.8,
      height: 0.912921,
    },
    labelGap: -22,
    label: { x: 243.5, y: 438.678 },
  },
  'swainsons-hawk': {
    bird: { x: 474.119, y: 580.533 },
    visibleBirdBounds: {
      x: 0.006289,
      y: 0.069091,
      width: 0.981132,
      height: 0.818182,
    },
    label: { x: 425.5, y: 653.678 },
  },
  'pacific-swift': {
    bird: { x: 1025.13, y: 379.151, rotate: -5.181 },
    visibleBirdBounds: {
      x: 0.07947,
      y: 0.052239,
      width: 0.844371,
      height: 0.914179,
    },
    label: { x: 1035, y: 450.678 },
  },
  'rainbow-bee-eater': {
    bird: { x: 1087.56, y: 775.463, rotate: -13.881 },
    visibleBirdBounds: {
      x: 0.051136,
      y: 0.034161,
      width: 0.78125,
      height: 0.754658,
    },
    label: { x: 1055, y: 793.678 },
  },
  'far-eastern-curlew': {
    bird: { x: 1287.936, y: 533.762 },
    visibleBirdBounds: {
      x: 0.1841,
      y: 0.281046,
      width: 0.638075,
      height: 0.43573,
    },
    labelGap: 4,
    label: { x: 1263, y: 590.678 },
  },
  'red-footed-falcon': {
    bird: { x: 859.039, y: 736.217, rotate: -11.81 },
    visibleBirdBounds: {
      x: 0.138743,
      y: 0.10101,
      width: 0.732984,
      height: 0.858586,
    },
    label: { x: 860.33, y: 779.322 },
  },
  'common-eider': {
    bird: { x: 836.735, y: 198.7 },
    label: { x: 859.83, y: 282.322 },
  },
  'northern-wheatear': {
    bird: { x: 789.904, y: 534.24 },
    label: { x: 722.33, y: 638.322 },
  },
  'indian-paradise-flycatcher': {
    bird: { x: 867.857, y: 578.443, rotate: -0.989 },
    visibleBirdBounds: {
      x: 0.013369,
      y: 0.098462,
      width: 0.97861,
      height: 0.796923,
    },
    label: { x: 814.887, y: 672.678 },
  },
  'sociable-lapwing': {
    bird: { x: 910.687, y: 321.539 },
    visibleBirdBounds: {
      x: 0.098039,
      y: 0.067194,
      width: 0.72549,
      height: 0.901186,
    },
    label: { x: 888.387, y: 383.678 },
  },
  'bar-headed-goose': {
    bird: { x: 1145.56, y: 348.49 },
    visibleBirdBounds: {
      x: 0.043228,
      y: 0.243333,
      width: 0.925072,
      height: 0.426667,
    },
    label: { x: 1125.39, y: 397.678 },
  },
  'grey-headed-albatross': {
    bird: { x: 1067, y: 768.644, rotate: 1.731, scale: 1.2 },
    visibleBirdBounds: {
      x: 0.168959,
      y: 0.297357,
      width: 0.620825,
      height: 0.422907,
    },
    label: { x: 1078, y: 835 },
  },
  'arctic-tern': {
    bird: { x: 639.227, y: 283 },
    visibleBirdBounds: {
      x: 0.066869,
      y: 0.079365,
      width: 0.829787,
      height: 0.844444,
    },
    label: { x: 669, y: 372 },
  },
  'south-polar-skua': {
    bird: { x: 289.165, y: 404.554 },
    visibleBirdBounds: {
      x: 0.015228,
      y: 0.022556,
      width: 0.893401,
      height: 0.958647,
    },
    label: { x: 235, y: 535 },
  },
} as const satisfies Record<FlywayMapSpeciesId, FlywayMapSpeciesPlacement>;

export type FlywayMapPlacementsConfig = typeof flywayMapPlacements;

export const flywayMapMotion = {
  /** Fade-out duration for the previous filter’s layers. */
  fadeOutSeconds: 0.3,
  /** Fade-in duration for the next filter’s layers. */
  fadeInSeconds: 0.38,
  /** Camera pan / zoom duration. */
  cameraSeconds: 0.75,
  /**
   * When the camera tween starts, relative to transition start.
   * `0` = move immediately with the outgoing fade.
   */
  cameraStartAt: 0,
  /**
   * Delay before swapping visibility and fading the next layers in.
   * Should be ≤ `fadeOutSeconds` so the handoff feels continuous.
   */
  fadeInDelaySeconds: 0.3,
  /** Selected-state badge scale at the start of fade-in. */
  badgeScaleFrom: 0.2,
  /** Extra seconds on the badge scale-in beyond `fadeInSeconds`. */
  badgeScaleExtraSeconds: 0.1,
} as const;

export type FlywayMapMotionConfig = typeof flywayMapMotion;

/**
 * Continuous comet trails on migration route strokes.
 * Duration per path = pathLength / speedUnitsPerSecond (shorter loops sooner).
 *
 * Top-level keys are the shared defaults. Optional flyway keys
 * (`americas`, `marine`, …) supply partial overrides for that flyway’s
 * routes only — merged over the defaults via `resolveFlywayMapComet`.
 * `maxFps` is always global (shared rAF loop).
 */
export type FlywayMapCometOptions = {
  /** Short dash copies per route (density vs smoothness trade-off). */
  segmentCount: number;
  /** Visible trail as a fraction of total path length. */
  trailFraction: number;
  /** Constant perceived speed in SVG path units per second. */
  speedUnitsPerSecond: number;
  /** Faint full-route guide under the animated trail. */
  guideOpacity: number;
  /** Desync loops with a stable per-path phase offset. */
  phaseSeed: boolean;
  /** Cap comet paint updates (rAF still ticks; work skipped between frames). */
  maxFps: number;
};

export type FlywayMapCometFlywayId = Exclude<FlywayMapCameraFilterId, 'all'>;

/** Per-flyway overrides — any subset of the shared options except `maxFps`. */
export type FlywayMapCometOverride = Partial<Omit<FlywayMapCometOptions, 'maxFps'>>;

type FlywayMapCometConfigShape = FlywayMapCometOptions &
  Partial<Record<FlywayMapCometFlywayId, FlywayMapCometOverride>>;

export const flywayMapComet = {
  segmentCount: 20,
  trailFraction: 0.2,
  speedUnitsPerSecond: 120,
  /**
   * Full-route guide under the animated comet. Kept translucent so the
   * moving head still reads, but high enough that the flyway silhouette
   * stays visible end-to-end (feedback: trails were too faint at 0.12).
   */
  guideOpacity: 0.28,
  phaseSeed: true,
  maxFps: 60,
  marine: {
    trailFraction: 0.1,
  },
} as const satisfies FlywayMapCometConfigShape;

export type FlywayMapCometConfig = typeof flywayMapComet;

export type FlywayMapRelativeCometTimingGroup = {
  /** Path whose existing length/speed duration remains the visual baseline. */
  referencePathId: string;
  /** Routes whose duration is derived from that baseline. */
  pathIds: readonly string[];
  /** Extra travel time applied to each derived route. */
  durationOffsetSeconds: number;
};

/**
 * Long African–Eurasian routes scale their speed to the short reference route,
 * then retain a one-second longer journey for a slightly calmer pace.
 */
export const flywayMapRelativeCometTimingGroups = [
  {
    referencePathId: FLYWAYS_MAP_AFRICAN_EURASIAN_TIMING_REFERENCE_PATH_ID,
    pathIds: FLYWAYS_MAP_AFRICAN_EURASIAN_LONG_PATH_IDS,
    durationOffsetSeconds: 1,
  },
] as const satisfies readonly FlywayMapRelativeCometTimingGroup[];

export type FlywayMapSplitPingPongRoute = {
  /** Authored SVG path id; the guide geometry itself remains unchanged. */
  splitPathId: string;
  /** Samples used once at init to locate the route's visual top junction. */
  junctionSampleCount: number;
};

/**
 * Routes whose authored guide splits into two branches. Their motion still
 * participates in the shared cycle for whichever flyway view is active.
 */
export const flywayMapSplitPingPongRoutes = [
  {
    splitPathId: FLYWAYS_MAP_AFRICAN_EURASIAN_SPLIT_PATH_ID,
    junctionSampleCount: 256,
  },
  {
    splitPathId: FLYWAYS_MAP_CENTRAL_ASIA_SPLIT_PATH_ID,
    junctionSampleCount: 256,
  },
] as const satisfies readonly FlywayMapSplitPingPongRoute[];

export type FlywayMapSelfIntersectingRouteCycle = {
  /** Authored closed SVG path whose crossing separates trunk and loop. */
  pathId: string;
  /** Samples used once at init before locally refining the crossing pair. */
  junctionSampleCount: number;
  /** Ignore nearby points and the authored start/end seam. */
  minimumCircularArcGapFraction: number;
  /** Fall back to ordinary continuous motion if no true crossing is found. */
  maximumIntersectionDistanceUnits: number;
};

/**
 * Routes that enter a crossing, split around a loop in both directions, merge
 * at the crossing, then continue along the closing trunk.
 */
export const flywayMapSelfIntersectingRouteCycles = [
  {
    pathId: FLYWAYS_MAP_ARCTIC_TERN_BRANCH_LOOP_PATH_ID,
    junctionSampleCount: 512,
    minimumCircularArcGapFraction: 0.1,
    maximumIntersectionDistanceUnits: 4,
  },
] as const satisfies readonly FlywayMapSelfIntersectingRouteCycle[];

/** Normalized outbound/return clock for every route visible in All Flyways. */
export const flywayMapAllFlywaysCycle = {
  cyclePauseSeconds: 0.2,
  speedMultiplier: 3,
} as const;

/** Shared outbound/return clock for every route visible in a selected view. */
export const flywayMapSelectedRouteCycle = {
  cyclePauseSeconds: 0.2,
} as const;

/** Accelerate the complete synchronized cycle when Marine Flyways is selected. */
export const flywayMapMarineCycle = {
  speedMultiplier: 2,
} as const;

/**
 * Selected Americas uses the closed route's vertical extrema as its shared
 * down/up boundaries, keeping the loop seamless while pacing the open routes.
 */
export const flywayMapAmericasDirectionalCycle = {
  pacingPathId: FLYWAYS_MAP_AMERICAS_SEAMLESS_LOOP_PATH_ID,
  verticalExtremaSampleCount: 512,
} as const;

/** Slightly slow Vector 155 so its travel phase stays closer to Vector 1. */
export const flywayMapAmericasSecondaryRouteTiming = {
  pathId: FLYWAYS_MAP_RUFOUS_HUMMINGBIRD_PATH_ID,
  speedMultiplier: 0.9,
} as const;

/** Routes that repeat their authored direction in both shared stages. */
export const flywayMapSameDirectionRoundTripPathIds = [
  FLYWAYS_MAP_ARCTIC_TERN_BRANCH_LOOP_PATH_ID,
  FLYWAYS_MAP_GREY_HEADED_ALBATROSS_PATH_ID,
  FLYWAYS_MAP_SOUTH_POLAR_SKUA_PATH_ID,
] as const satisfies readonly string[];

/**
 * Closed overview routes split into shared downward and upward phases instead
 * of completing a full loop inside both halves of the round trip.
 */
export const flywayMapAllFlywaysDirectionalCycle = {
  pathIds: [
    FLYWAYS_MAP_ARCTIC_TERN_BRANCH_LOOP_PATH_ID,
    FLYWAYS_MAP_SOUTH_POLAR_SKUA_PATH_ID,
  ],
  verticalExtremaSampleCount: 512,
} as const;

/** Apply the same down/up route phases when Marine Flyways is selected. */
export const flywayMapMarineDirectionalCycle = flywayMapAllFlywaysDirectionalCycle;

/** Authored paths whose ordinary continuous comet follows decreasing distance. */
export const flywayMapReversedCometPathIds = [
  FLYWAYS_MAP_SOUTH_POLAR_SKUA_PATH_ID,
] as const satisfies readonly string[];

/** Closed routes whose continuous comet must wrap across the authored seam. */
export const flywayMapSeamlessLoopPathIds = [
  FLYWAYS_MAP_AMERICAS_SEAMLESS_LOOP_PATH_ID,
] as const satisfies readonly string[];

/**
 * Resolve comet options for a flyway (defaults + optional per-flyway override).
 * Omit `flywayId` for the shared defaults only.
 */
export function resolveFlywayMapComet(
  flywayId?: FlywayMapCometFlywayId,
  config: FlywayMapCometConfigShape = flywayMapComet,
): FlywayMapCometOptions {
  const base: FlywayMapCometOptions = {
    segmentCount: config.segmentCount,
    trailFraction: config.trailFraction,
    speedUnitsPerSecond: config.speedUnitsPerSecond,
    guideOpacity: config.guideOpacity,
    phaseSeed: config.phaseSeed,
    maxFps: config.maxFps,
  };
  if (!flywayId) return base;

  const override = config[flywayId];
  if (!override) return base;

  return { ...base, ...override };
}
